// @flow

import * as React from 'react';
import Box from 'sail/Box';
import {ButtonLink} from 'sail/Button';
import {useAccount, useOverlay} from 'docs/data';
import {VSCodeButton} from 'docs/shell/sidebar/xray/VSCodeButton';
import {XRayOverlayContainer} from 'docs/shell/sidebar/xray/XRayOverlayContainer';

import type {
  Node as MarkdocAstNode,
  ValidationError as MarkdocSchemaError,
} from '@markdoc/markdoc';

import './Error.scss';

type ErrorProps = {|
  node: MarkdocAstNode,
  errors: Array<MarkdocSchemaError>,
  children: React.Node,
|};

export const Error = ({node, errors, children}: ErrorProps) => {
  const {account} = useAccount();
  const [overlays] = useOverlay();

  if (node?.inline || !account.xray || !overlays.errors || !node.location) {
    return children ?? null;
  }

  const {
    file = '<unknown file>',
    start: {line},
  } = node.location;

  const tag = node.type.replace('tag:', '');
  const location = line ? `${file}:${line}` : file;

  const actions = (
    <>
      <Box className="Error-toolbar" flex={{direction: 'row'}}>
        <VSCodeButton path={location} />

        {node.type.startsWith('tag:') && (
          <ButtonLink
            label={
              <>
                <span>Read docs for </span>
                <code>{tag}</code>
              </>
            }
            href={`https://site-admin.stripe.com/docs/markdoc/tags/${tag}`}
            icon="external"
            iconPosition="right"
            target="_blank"
          />
        )}
      </Box>

      <ul className="Error-errors">
        {errors.map(({message}, index) => (
          <li key={index}> {message}</li>
        ))}
      </ul>
    </>
  );

  return (
    <XRayOverlayContainer
      icon="cross"
      title={`Error: ${location}`}
      color="red"
      className="Error"
      actions={actions}
    >
      {children}
    </XRayOverlayContainer>
  );
};
